import React from 'react';
import { Section, Button, FadeIn, SEO } from '../components/UI';
import { 
  Users, 
  Palette, 
  HeartHandshake, 
  Sparkles, 
  ShieldCheck, 
  BookOpen,
  ArrowRight,
  CheckCircle2
} from 'lucide-react';

export default function ForedragWorkshops() {
  return (
    <div className="pt-20">
      <SEO 
        title="Foredrag & Workshops" 
        description="Inddragende foredrag og kreative workshops om sorg, død, afsked og livet. Tilpasset enhver forsamling – for borgere, foreninger, fagpersoner og skoler."
      />

      {/* Hero Section */}
      <Section className="bg-transparent pt-8 md:pt-12 pb-12 md:pb-24 relative overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center relative z-10">
          <div className="lg:col-span-6">
            <FadeIn className="pt-12 md:pt-0">
              <span className="text-sorg-earth font-medium tracking-[0.2em] uppercase text-xs mb-6 block">
                Oplæg, Samtalerum & Temadage
              </span>
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-serif text-sorg-dark mb-8 leading-[1.15]">
                Foredrag &amp; Workshops
              </h1>
              <p className="text-xl text-sorg-dark/75 leading-relaxed mb-6">
                Et levende, nærværende rum. Jeg tilbyder skræddersyede foredrag og workshops – skabt til at inddrage, vække nysgerrighed og give et sprog for det, vi ofte bliver stille omkring.
              </p>
              <p className="text-base text-sorg-dark/65 leading-relaxed mb-10">
                For når vi tør tale åbent om døden, tabet og sorgen, handler samtalen i virkeligheden om noget endnu større: hvordan vi er der for hinanden, og hvordan vi ønsker at leve.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button to="/kontakt" className="px-6 py-2.5 md:px-8 md:py-3">Book eller forhør nærmere</Button>
                <Button to="#formater" variant="secondary" className="px-6 py-2.5 md:px-8 md:py-3">Se muligheder</Button>
              </div>
            </FadeIn>
          </div>
          
          <div className="lg:col-span-6">
            <FadeIn direction="left" delay={0.2}>
              <div className="relative">
                <div className="absolute -inset-4 bg-sorg-beige/25 rounded-[2rem] blur-2xl -z-10 transform rotate-1"></div>
                <div className="aspect-[4/3] md:aspect-[16/11] w-full overflow-hidden rounded-[2rem] soft-shadow-lg border border-white/60 bg-sorg-sand/20">
                  <img 
                    src="/cdn-cgi/image/width=1000,quality=85,format=auto/_assets/workshop-faellesskab-haender-cirkel.jpg" 
                    alt="Gruppe i samtalecirkel set i varmt lys uden synlige ansigter, der symboliserer nærvær og fællesskab" 
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000"
                  />
                </div>
                <div className="absolute -bottom-6 -left-4 md:-left-6 bg-white/90 backdrop-blur-md p-5 md:p-6 rounded-2xl soft-shadow border border-white/60 max-w-[260px]">
                  <p className="text-xs uppercase tracking-widest text-sorg-earth font-semibold mb-1">Filosofien</p>
                  <p className="text-sm font-serif text-sorg-dark italic">
                    "Vi løfter hinanden, når vi tør dele det, der ellers er svært at sætte ord på."
                  </p>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </Section>

      {/* De to hovedformater */}
      <Section id="formater" glass>
        <div className="max-w-6xl mx-auto space-y-24">
          {/* Format 1: Foredrag */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 space-y-6">
              <FadeIn>
                <div className="inline-flex items-center space-x-2 px-3.5 py-1.5 rounded-full bg-sorg-beige/40 text-sorg-earth text-xs uppercase tracking-wider font-medium mb-2">
                  <HeartHandshake size={14} />
                  <span>Tilpassede Foredrag &amp; Oplæg</span>
                </div>
                <h3 className="text-3xl md:text-4xl font-serif text-sorg-dark">
                  Foredrag og oplæg tilpasset enhver forsamling
                </h3>
                <p className="text-sorg-dark/75 text-base leading-relaxed">
                  Mine foredrag og oplæg tilpasses altid den konkrete forsamling. De henvender sig både til helt almindelige borgere og pårørende, der ønsker et trygt rum til at tale om døden og sorgen, samt til faggrupper og organisationer – for eksempel frivillige vågere, plejepersonale, hospicemedarbejdere, patientforeninger eller menighedsråd.
                </p>
                <p className="text-sorg-dark/75 text-base leading-relaxed">
                  Uanset om publikum er borgere med personlige erfaringer eller fagfolk og frivillige i praksis, dykker vi ned i det væsentlige: Hvordan møder vi afskeden og livets afslutning? Hvad sanser og mærker vi ved dødslejet? Hvordan åbner vi for de svære, men livsnødvendige samtaler om sorg og tab? Og hvordan drager vi omsorg for hinanden og os selv i mødet med det sårbare?
                </p>

                <div className="pt-2">
                  <h4 className="font-serif text-lg text-sorg-dark mb-3">Eksempler på temaer:</h4>
                  <ul className="space-y-2.5">
                    {[
                      'At være til stede ved livets afslutning – nærvær uden ord',
                      'Hvordan får vi samtalen i gang, når sorgen fylder rummet?',
                      'De mange ansigter af sorg: At rumme forskelligheden hos dem, vi møder',
                      'At bære det svære sammen – støtte til pårørende og vågere',
                      'Dødsdoulaens rolle som brobygger og roskabende ledsager'
                    ].map((punkt, idx) => (
                      <li key={idx} className="flex items-start space-x-3 text-sorg-dark/75 text-sm">
                        <CheckCircle2 size={18} className="text-sorg-earth flex-shrink-0 mt-0.5" />
                        <span>{punkt}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="pt-4">
                  <Button to="/kontakt" variant="primary">Forespørg på foredrag</Button>
                </div>
              </FadeIn>
            </div>

            <div className="lg:col-span-6">
              <FadeIn direction="left" delay={0.2}>
                <div className="bg-white/70 p-8 md:p-10 rounded-3xl border border-sorg-beige/60 soft-shadow space-y-6">
                  <div className="border-b border-sorg-beige/40 pb-5">
                    <span className="text-xs uppercase tracking-widest text-sorg-earth font-medium">Praktisk ramme</span>
                    <h4 className="text-2xl font-serif text-sorg-dark mt-1">Foredrag &amp; Oplægsformat</h4>
                  </div>
                  <dl className="space-y-4 text-sm">
                    <div>
                      <dt className="font-medium text-sorg-dark">Varighed:</dt>
                      <dd className="text-sorg-dark/70">Tilpasses fleksibelt efter jeres program, ønsker og behov.</dd>
                    </div>
                    <div>
                      <dt className="font-medium text-sorg-dark">Deltagere:</dt>
                      <dd className="text-sorg-dark/70">For både mindre intime grupper og større forsamlinger – formatet formes efter gruppens størrelse, så det nære rum bevares.</dd>
                    </div>
                    <div>
                      <dt className="font-medium text-sorg-dark">Lokation:</dt>
                      <dd className="text-sorg-dark/70">Jeg kommer gerne ud til jeres lokaler, foreningshuse eller arbejdspladser i hele landet efter aftale.</dd>
                    </div>
                    <div className="p-4 bg-sorg-beige/25 rounded-2xl border border-sorg-beige/30">
                      <p className="text-xs text-sorg-dark/80 italic leading-relaxed">
                        "Oplægget bliver altid skræddersyet, så det matcher jeres behov og de konkrete udfordringer, jeres deltagere sidder med i hverdagen."
                      </p>
                    </div>
                  </dl>
                </div>
              </FadeIn>
            </div>
          </div>

          {/* Format 2: Skoleworkshops & Temadage */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center pt-8 border-t border-sorg-beige/40">
            <div className="lg:col-span-6 lg:order-2 space-y-6">
              <FadeIn>
                <div className="inline-flex items-center space-x-2 px-3.5 py-1.5 rounded-full bg-sorg-beige/40 text-sorg-earth text-xs uppercase tracking-wider font-medium mb-2">
                  <Sparkles size={14} />
                  <span>Sorgskab for Skoler &amp; Unge</span>
                </div>
                <h3 className="text-3xl md:text-4xl font-serif text-sorg-dark">
                  Workshops i kreativitetens og refleksionens tegn
                </h3>
                <p className="text-sorg-dark/75 text-lg leading-relaxed">
                  Vi lærer børn og unge om uddannelse, sundhed og fremtid – men sjældent om døden og sorgen, selvom de fleste vil møde den før eller siden.
                </p>
                <p className="text-sorg-dark/70 text-base leading-relaxed">
                  Workshoppen er en temadag, hvor vi giver de unge et sprog for det svære gennem samtale, musik, symboler og kreative stationer. Det handler ikke om at gøre døden uhyggelig, men om at bruge afskeden som et spejl: Hvad er værdifuldt for mig? Hvad vil jeg gerne huskes for? Og hvordan kan vi passe på en kammerat, der har mistet?
                </p>

                <div className="space-y-3 pt-2">
                  <div className="flex items-start space-x-3 text-sorg-dark/75 text-sm">
                    <BookOpen size={18} className="text-sorg-earth flex-shrink-0 mt-0.5" />
                    <span><strong>Målgruppe:</strong> Udskolingsklasser i folkeskolen, efterskoler, gymnasier eller ungdomsuddannelser.</span>
                  </div>
                  <div className="flex items-start space-x-3 text-sorg-dark/75 text-sm">
                    <Palette size={18} className="text-sorg-earth flex-shrink-0 mt-0.5" />
                    <span><strong>Kreativ forankring:</strong> Hænderne får lov at arbejde, når ordene slipper op.</span>
                  </div>
                  <div className="flex items-start space-x-3 text-sorg-dark/75 text-sm">
                    <ShieldCheck size={18} className="text-sorg-earth flex-shrink-0 mt-0.5" />
                    <span><strong>Fast tryghedsramme:</strong> Tæt forventningsafstemning med skolens lærere og fuld frivillighed for eleverne.</span>
                  </div>
                </div>

                <div className="pt-4">
                  <Button to="/kontakt" variant="primary">Book en skole-workshop</Button>
                </div>
              </FadeIn>
            </div>

            <div className="lg:col-span-6 lg:order-1">
              <FadeIn direction="right" delay={0.2}>
                <div className="relative">
                  <div className="aspect-[4/3] md:aspect-[16/11] w-full overflow-hidden rounded-[2rem] soft-shadow-lg border border-white/60 bg-sorg-sand/20">
                    <img 
                      src="/cdn-cgi/image/width=1000,quality=85,format=auto/_assets/unge-workshop-bagfra-ryggen-til.jpg" 
                      alt="Børn og unge fordybet i kreativ workshop med sten set med ryggen til uden synlige ansigter" 
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000"
                    />
                  </div>
                  <div className="mt-6 p-6 bg-white/80 rounded-2xl border border-sorg-beige/40">
                    <p className="text-sm font-serif text-sorg-dark italic text-center">
                      "En dag om døden. Som i virkeligheden handler om livet."
                    </p>
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </Section>



      {/* Tryghed & Forventningsafstemning */}
      <Section className="bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="bg-sorg-beige/20 border border-sorg-beige/50 rounded-3xl p-8 md:p-14">
            <FadeIn>
              <div className="max-w-3xl">
                <span className="text-sorg-earth font-medium tracking-[0.2em] uppercase text-xs mb-3 block">
                  Etik &amp; Tryghed
                </span>
                <h3 className="text-2xl md:text-4xl font-serif text-sorg-dark mb-6">
                  Trygheden er grundstenen i ethvert arrangement
                </h3>
                <p className="text-sorg-dark/75 leading-relaxed mb-6">
                  Blandt både voksne og unge vil der altid være deltagere, som bærer på et personligt tab, aktuel sygdom i familien eller dybe bekymringer.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4 text-sm">
                  <div className="space-y-2">
                    <p className="font-semibold text-sorg-dark">Før dagen:</p>
                    <p className="text-sorg-dark/70 leading-relaxed">Vi forventningsafstemmer målgruppens sammensætning, særlige sårbarheder og det pædagogiske/faglige formål.</p>
                  </div>
                  <div className="space-y-2">
                    <p className="font-semibold text-sorg-dark">Undervejs:</p>
                    <p className="text-sorg-dark/70 leading-relaxed">Fuldstændig frivillighed. Man må deltage med ørerne, tage en pause eller trække sig til en kendt kontaktperson, hvis det bliver overvældende.</p>
                  </div>
                  <div className="space-y-2">
                    <p className="font-semibold text-sorg-dark">Efter forløbet:</p>
                    <p className="text-sorg-dark/70 leading-relaxed">Vi afslutter altid med at lande i hverdagen, fællesskabet og håbet, så ingen forlader rummet med en uafsluttet følelse.</p>
                  </div>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </Section>

      {/* CTA Section */}
      <Section className="bg-sorg-sand/40 backdrop-blur-sm text-center">
        <div className="max-w-3xl mx-auto">
          <FadeIn>
            <span className="text-sorg-earth font-medium tracking-[0.2em] uppercase text-xs mb-3 block">
              Kontakt &amp; Booking
            </span>
            <h2 className="text-3xl md:text-5xl font-serif text-sorg-dark mb-6">
              Skal vi skabe et nærværende oplæg hos jer?
            </h2>
            <p className="text-sorg-dark/70 text-lg leading-relaxed mb-10">
              Uanset om du søger et foredrag til vågetjenesten, et fagligt oplæg til kollegerne eller en kreativ temadag for skoleklasser, er du velkommen til at række ud for en uforpligtende samtale om mulighederne.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button to="/kontakt" className="px-8 py-3.5 flex items-center space-x-2">
                <span>Kontakt mig for en aftale</span>
                <ArrowRight size={16} />
              </Button>
              <Button to="/om-asha" variant="secondary" className="px-8 py-3.5">
                Læs mere om Asha
              </Button>
            </div>
          </FadeIn>
        </div>
      </Section>
    </div>
  );
}
